import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { STAGE_VAR, STAGE_ICON, TYPE_GLYPH, SOURCE_ICON, priorityVar } from '@/lib/stage'
import { AppIcon } from './icon-map'
import type { Assignee } from '@/types/github'

export function StageDot({ stage, className }: { stage: string | null; className?: string }) {
  const varName = (stage && STAGE_VAR[stage]) || '--st-triage'
  return (
    <span
      className={cn('inline-block size-2 shrink-0 rounded-full', className)}
      style={{ background: `var(${varName})` }}
      aria-hidden
    />
  )
}

export function StageBadge({ stage }: { stage: string | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <StageDot stage={stage} />
      {stage ?? '—'}
    </span>
  )
}

export function StageIcon({ stage, className }: { stage: string | null; className?: string }) {
  return (
    <AppIcon
      name={stage ? STAGE_ICON[stage] : undefined}
      className={cn('size-3.5', className)}
      style={{ color: `var(${(stage && STAGE_VAR[stage]) || '--st-triage'})` }}
    />
  )
}

/** The canonical priority colour dot. Default size matches the chip; pass a
 *  `size-*` className to vary (board bands use a larger one). */
export function PriorityDot({ priority, className }: { priority: string | null; className?: string }) {
  return (
    <span
      className={cn('size-1.5 rounded-full', className)}
      style={{ background: `var(${priorityVar(priority)})` }}
      aria-hidden
    />
  )
}

export function PriorityChip({ priority }: { priority: string | null }) {
  if (!priority) return null
  return (
    <span className="inline-flex items-center gap-1 rounded-sm border px-1.5 py-px text-[11px] font-medium">
      <PriorityDot priority={priority} />
      {priority}
    </span>
  )
}

export function TypeGlyph({ typeName, className }: { typeName: string | null; className?: string }) {
  const glyph = typeName ? TYPE_GLYPH[typeName] : undefined
  return (
    <span aria-label={typeName ?? 'Task'} className="inline-flex shrink-0">
      <AppIcon
        name={glyph?.icon ?? 'square-check'}
        className={cn('size-3.5', className)}
        style={{ color: `var(${glyph?.varName ?? '--st-ready'})` }}
      />
    </span>
  )
}

export function SourceGlyph({ source, className }: { source: string | null; className?: string }) {
  if (!source) return null
  return (
    <span className={cn('text-faint-foreground inline-flex items-center gap-1 text-[11px]', className)}>
      <AppIcon name={SOURCE_ICON[source]} className="size-3" />
      {source}
    </span>
  )
}

/** Deterministic avatar tint from the login (hash → hue). */
function loginHue(login: string): number {
  let h = 0
  for (let i = 0; i < login.length; i++) h = (h * 31 + login.charCodeAt(i)) % 360
  return h
}

export function UserAvatar({ user, className }: { user: Assignee; className?: string }) {
  const hue = loginHue(user.login)
  const initials = (user.name ?? user.login)
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return (
    <Avatar className={cn('size-5 border-card border', className)} title={user.name ?? user.login}>
      <AvatarImage src={user.avatarUrl} alt={user.login} />
      <AvatarFallback
        className="text-[9px] font-semibold"
        style={{
          background: `oklch(0.9 0.05 ${hue})`,
          color: `oklch(0.38 0.09 ${hue})`,
        }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  )
}

export function AvatarGroup({ users, max = 3 }: { users: Assignee[]; max?: number }) {
  if (users.length === 0) return null
  const shown = users.slice(0, max)
  const extra = users.length - shown.length
  return (
    <span className="flex items-center -space-x-1">
      {shown.map((u) => (
        <UserAvatar key={u.login} user={u} />
      ))}
      {extra > 0 && (
        <span className="bg-muted text-muted-foreground border-card z-10 inline-flex size-5 items-center justify-center rounded-full border text-[9px] font-semibold">
          +{extra}
        </span>
      )}
    </span>
  )
}
